import { useMemo, useState } from 'react';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { InlineMessage, Panel } from '@bigcommerce/big-design';
import { UploadContainer } from '../../Orders/OrdersEditor/components/StyledComponents';
import {
	readFileAsync,
	useImportFitment,
	validateImportFile,
} from '../hooks/useImportFitment';

export default function Import() {
	const [fileToUpload, setFileToUpload] = useState(null);
	const [fileToUploadName, setFileToUploadName] = useState(null);
	const [validationRecords, setValidationRecords] = useState([]);
	const [isLoading, dispatchFileImport] = useImportFitment(
		function onImportComplete() {
			setFileToUpload(null);
			setFileToUploadName(null);
			setValidationRecords([]);
		},
	);
	const {
		getRootProps,
		getInputProps,
		isFocused,
		isDragAccept,
		isDragReject,
	} = useDropzone({
		onDrop: async ([acceptedFile]) => {
			const csvData = await readFileAsync(acceptedFile);
			setFileToUploadName(acceptedFile.name);
			setValidationRecords(await validateImportFile(csvData));
			setFileToUpload(csvData);
		},
		disabled: isLoading !== false,
	});

	const validationErrorsCount = useMemo(
		() => validationRecords.filter((row) => row.errors !== '').length,
		[validationRecords],
	);

	let dropzoneMessage = (
		<p>Drop Formatted CSV File Here or Click to Select File</p>
	);

	if (fileToUpload !== null)
		dropzoneMessage = <p>Ready To Import {fileToUploadName}</p>;

	if (validationErrorsCount > 0) {
		dropzoneMessage = (
			<p>
				Unable to import file, validation errors exist. Please fix
				issues and re-upload.
			</p>
		);
	}

	if (isLoading !== false) dropzoneMessage = <p>{isLoading}</p>;

	return (
		<Panel
			header="Import"
			action={{
				variant: 'primary',
				text: 'Upload',
				isLoading: isLoading !== false,
				disabled:
					isLoading !== false ||
					fileToUpload === null ||
					validationErrorsCount > 0,
				onClick: () => dispatchFileImport(fileToUpload),
			}}
		>
			<UploadContainer
				{...getRootProps({ isFocused, isDragAccept, isDragReject })}
			>
				<div {...getRootProps({ className: 'dropzone' })}>
					<input {...getInputProps()} />
					{dropzoneMessage}
				</div>
			</UploadContainer>
			{validationErrorsCount > 0 && (
				<InlineMessage
					marginTop="small"
					type="error"
					messages={[
						{
							text: `${validationErrorsCount} error${
								validationErrorsCount > 1 ? 's' : ''
							} found. Please download the report to see the errors.`,
						},
					]}
					actions={[
						{
							text: `Download Report for ${fileToUploadName}`,
							variant: 'subtle',
							onClick: () => {
								saveAs(
									new Blob(
										[Papa.unparse(validationRecords)],
										{
											type: 'text/csv;charset=utf-8;',
										},
									),
									fileToUploadName,
								);
							},
						},
					]}
				/>
			)}
		</Panel>
	);
}
